import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { type StitchFiltersHistoryRecord, HistoryService } from '@modules/common/services/history.service';
import { type Connection } from '@modules/connections/types/connection';
import { type Knot } from '@modules/knots/types/knot';
import { KnowledgePanelService } from '@modules/knowledge/services/knowledge-panel.service';
import { KpResultCountsService } from '@modules/knowledge/services/kp-result-counts.service';
import { KPListType } from '@modules/knowledge/types/kp-list-type';
import { KP_SELECTION_DRIVEN } from '@modules/knowledge/types/kp-selection-driven';
import { ADVANCED_SEARCH_NAMESPACE } from '@modules/search/injection-tokens/advanced-search-namespace.injection-token';
import { HOST_ADVANCED_SEARCH_SERVICE } from '@modules/search/injection-tokens/host-advanced-search-service.injection-token';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateKey } from '@modules/settings/types/state-key';
import { STICKY_INCLUDES_PENDING_FOLLOWUP } from '@modules/common/components/list/base-stitch-list.component';
import { type Tag } from '@modules/tags/types/tag';
import { type StitchItemSummaryDto } from '@sharedlib/dtos/dto/StitchItemSummaryDto';

const NO_MARKERS: StitchFiltersHistoryRecord = { knots: [], tags: [], connections: [] };

/**
 * The Knowledge Panel's results pane for a selection the host gives it: the type button with its counts and the selected type's
 * list. It is the KP's own right pane (`knowledge-panel.component.html`, `@switch (kpListType())`, copied unchanged) and its own
 * sections, header, counts service and data service. Only the selection differs: the KP reads it from the panel's store; here it is
 * an input, written into this component's OWN history and panel service (component-scoped, as the Dynamic Panel and the modals
 * scope theirs, `devdoc/specs/knowledge-panel-selection-scopes.md`), so it never reads or changes the Knowledge Panel's selection.
 */
@Component({
  selector: 'app-kp-selection-results',
  templateUrl: './kp-selection-results.component.html',
  standalone: false,
  host: { class: 'flex h-full min-h-0 flex-col' },
  providers: [
    // Own selection store and service: the same pair the Dynamic Panel provides (`dynamic-panel.component.ts:99-101`).
    HistoryService,
    KnowledgePanelService,
    // The providers of `KnowledgePanelComponent` (`knowledge-panel.component.ts:40-57`), with a namespace of its own so the KP's saved search is left alone.
    AdvancedSearchService,
    { provide: ADVANCED_SEARCH_NAMESPACE, useValue: 'atlas-kp-sources' },
    KpResultCountsService,
    {
      provide: HOST_ADVANCED_SEARCH_SERVICE,
      useFactory: () => inject(AdvancedSearchService, { skipSelf: true, optional: true }),
    },
    { provide: STICKY_INCLUDES_PENDING_FOLLOWUP, useValue: true },
    // No item is open in the 3rd panel: the selection alone drives the lists.
    { provide: KP_SELECTION_DRIVEN, useValue: true },
  ],
})
export class KpSelectionResultsComponent {
  protected readonly KPListType = KPListType;
  protected readonly resultsTypeStateKey = StateKey.kpResultsStitchType;
  protected readonly kpListType = signal(KPListType.message);

  /** The selection, as the KP's own Knot, Tag and Connection objects. */
  public readonly selection = input.required<{ knots: Knot[]; tags: Tag[]; connections: Connection[] }>();

  private readonly history = inject(HistoryService);
  private readonly kpService = inject(KnowledgePanelService);

  // Read through the service, as the KP does (`knowledge-panel.component.ts:97-103`).
  private readonly queryFilter = toSignal(this.kpService.getQueryFilter(), { initialValue: NO_MARKERS });
  protected readonly resultsKnots = computed(() => this.queryFilter().knots);
  protected readonly resultsTags = computed(() => this.queryFilter().tags);
  protected readonly resultsConnections = computed(() => this.queryFilter().connections);

  constructor() {
    inject(AdvancedSearchService).resetState();
    effect(() => this.history.updateHistory({ ...this.selection() }));
  }

  protected handleClickItem(_item: StitchItemSummaryDto): void {
    // As the Knowledge Panel: a click opens nothing here.
  }
}
